import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, Shield, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

const FLAG_LABELS = {
  defamation_risk: 'Verleumdungsrisiko',
  insults: 'Beleidigungen',
  names: 'Namen genannt',
  false_claims: 'Unbewiesene Behauptungen',
  threats: 'Drohungen',
};

const SparklesIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" />
  </svg>
);

export default function AnalysisStep({
  riskAnalysis, freeText, rewrittenText, useRewritten,
  setUseRewritten, onRewrite, rewriting, onBack, onNext
}) {
  return (
    <motion.div
      key="negative-analysis"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {riskAnalysis && riskAnalysis.risk_score > 30 && (
        <Alert variant={riskAnalysis.risk_score > 60 ? 'destructive' : 'default'} className={riskAnalysis.risk_score > 60 ? '' : 'border-yellow-400 bg-yellow-50'}>
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>Hinweis zu Ihrem Text</AlertTitle>
          <AlertDescription>{riskAnalysis.short_advice}</AlertDescription>
        </Alert>
      )}

      <Card className="card-shadow">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent" />
            <CardTitle className="text-lg">Sachlichkeitsprüfung</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {riskAnalysis && (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className={`text-2xl font-bold ${riskAnalysis.fairness_score >= 60 ? 'text-green-500' : riskAnalysis.fairness_score >= 30 ? 'text-yellow-500' : 'text-red-500'}`} data-testid="risk-panel-fairness-score">
                  {riskAnalysis.fairness_score}%
                </div>
                <p className="text-xs text-muted-foreground">Sachlichkeit</p>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${riskAnalysis.emotion_score <= 40 ? 'text-green-500' : riskAnalysis.emotion_score <= 70 ? 'text-yellow-500' : 'text-red-500'}`}>
                  {riskAnalysis.emotion_score}%
                </div>
                <p className="text-xs text-muted-foreground">Emotionalität</p>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${riskAnalysis.risk_score <= 30 ? 'text-green-500' : riskAnalysis.risk_score <= 60 ? 'text-yellow-500' : 'text-red-500'}`} data-testid="risk-panel-score">
                  {riskAnalysis.risk_score}%
                </div>
                <p className="text-xs text-muted-foreground">Risiko</p>
              </div>
            </div>
          )}

          {riskAnalysis?.flags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {riskAnalysis.flags.map((flag, i) => (
                <Badge key={i} variant="secondary">
                  {FLAG_LABELS[flag] || flag}
                </Badge>
              ))}
            </div>
          )}

          {freeText && (
            <div className="space-y-3 pt-4 border-t">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">
                    {useRewritten ? 'Neutraler Text:' : 'Ihr Originaltext:'}
                  </label>
                  {useRewritten && (
                    <Button variant="ghost" size="sm" onClick={() => setUseRewritten(false)}>
                      Original anzeigen
                    </Button>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-muted text-sm">
                  {useRewritten ? rewrittenText : freeText}
                </div>
              </div>

              {riskAnalysis && riskAnalysis.risk_score > 20 && !useRewritten && (
                <Button variant="secondary" onClick={onRewrite} disabled={rewriting} className="w-full" data-testid="rewrite-button">
                  {rewriting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SparklesIcon className="w-4 h-4 mr-2" />}
                  Professionell und rechtssicher formulieren
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button onClick={onNext} className="flex-1">
          Weiter
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </motion.div>
  );
}
